import { useCallback, useEffect, useState } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

const DISMISS_KEY = 'uroute.driver.installDismissed';

let deferred: BeforeInstallPromptEvent | null = null;
let dismissed = false;
let updateReady = false;
let updateApplier: (() => Promise<void>) | null = null;

const installListeners = new Set<() => void>();
const updateListeners = new Set<() => void>();

function emit(listeners: Set<() => void>): void {
  for (const fn of listeners) fn();
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

if (typeof window !== 'undefined') {
  dismissed = readDismissed();
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit(installListeners);
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit(installListeners);
  });
}

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string } | undefined;
    if (data?.type === 'UPDATE_AVAILABLE') announceUpdateAvailable();
  });
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  if (typeof ios === 'boolean') return ios;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iPhone = /iPad|iPhone|iPod/.test(ua);
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iPhone || iPadOs;
}

export function isIosSafari(): boolean {
  if (!isIos()) return false;
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)) return false;
  return /Safari/.test(ua);
}

export function dismissInstall(): void {
  dismissed = true;
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* private mode — keep it in memory only */
  }
  emit(installListeners);
}

export function announceUpdateAvailable(): void {
  updateReady = true;
  emit(updateListeners);
}

export function onUpdateAvailable(fn: () => void): () => void {
  updateListeners.add(fn);
  if (updateReady) fn();
  return () => {
    updateListeners.delete(fn);
  };
}

export function setUpdateApplier(fn: (() => Promise<void>) | null): void {
  updateApplier = fn;
}

export async function applyUpdate(): Promise<void> {
  if (updateApplier) {
    await updateApplier();
    return;
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const waiting = registration?.waiting;
  if (!waiting) {
    await registration?.update();
    return;
  }
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export interface InstallState {
  /** The browser fired beforeinstallprompt — we can show an install button. */
  canInstall: boolean;
  installed: boolean;
  /** The driver dismissed the install banner on this device. */
  dismissed: boolean;
  /** iOS never fires beforeinstallprompt — the banner explains how to install. */
  ios: boolean;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  dismiss: () => void;
}

export function useInstall(): InstallState {
  const [canInstall, setCanInstall] = useState(Boolean(deferred));
  const [installed, setInstalled] = useState(isStandalone());
  const [hidden, setHidden] = useState(dismissed);
  const [ios] = useState(isIos());

  useEffect(() => {
    const onChange = () => {
      setCanInstall(Boolean(deferred));
      setInstalled(isStandalone());
      setHidden(dismissed);
    };
    installListeners.add(onChange);
    const mq = window.matchMedia('(display-mode: standalone)');
    mq.addEventListener('change', onChange);
    return () => {
      installListeners.delete(onChange);
      mq.removeEventListener('change', onChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferred) return 'unavailable';
    const event = deferred;
    deferred = null;
    emit(installListeners);
    try {
      await event.prompt();
      const choice = await event.userChoice;
      setInstalled(isStandalone());
      if (choice.outcome === 'accepted') dismissInstall();
      return choice.outcome;
    } catch {
      return 'dismissed';
    }
  }, []);

  const dismiss = useCallback(() => {
    dismissInstall();
  }, []);

  return { canInstall, installed, dismissed: hidden, ios, promptInstall, dismiss };
}
